import { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getServices, getCompanySettings } from '../services/api';

const whyChooseDefaults = [
  'Enhance equipment lifespan',
  'Reduce operational downtime',
  'Cost-effective component recovery',
  'OEM-standard remanufacturing quality',
  'Sustainable process and waste reduction',
];

const fallbackServices = [
  { name: 'Remanufacture', desc: 'Full overhaul to restore factory-new performance and extend the operational lifespan of existing machinery.', image: 'https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?w=600&q=80' },
  { name: 'Trading', desc: 'Spare part trading, we will provide everything that you requested for your industrial needs.', image: 'https://images.unsplash.com/photo-1565891741441-64926e441838?w=600&q=80' },
  { name: 'Salvage', desc: 'Buying used component or unit CAT, Komatsu and HITACHI for sustainable recovery.', image: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=600&q=80' },
  { name: 'Repair KIT', desc: 'Providing all spare part you need for repairing a component with OEM-standard quality.', image: 'https://images.unsplash.com/photo-1537462715315-eb5b1e37e0ce?w=600&q=80' },
  { name: 'Machining', desc: 'Precision machining services for custom parts and components to meet exact specifications.', image: 'https://images.unsplash.com/photo-1565793298747-13c1f1a43cc4?w=600&q=80' },
];

const Services = () => {
  const sectionRef = useRef(null);
  const [services, setServices] = useState(fallbackServices);
  const [sectionHeading, setSectionHeading] = useState('Our Services');
  const [sectionDesc, setSectionDesc] = useState('Innovative solutions designed to rebuild, restore, and extend the life of your machinery with precision and quality.');
  const [whyPoints, setWhyPoints] = useState(whyChooseDefaults);
  const [centerServiceIndex, setCenterServiceIndex] = useState(0);

  useEffect(() => {
    getCompanySettings().then(data => {
      if (data) {
        if (data.services_heading) setSectionHeading(data.services_heading);
        if (data.services_description) setSectionDesc(data.services_description);
        if (data.services_why_points) {
          try {
            const pts = JSON.parse(data.services_why_points);
            if (Array.isArray(pts) && pts.length > 0) setWhyPoints(pts);
          } catch {}
        }
      }
    });
    getServices().then(data => {
      if (data && data.length > 0) {
        setServices(data.map((s) => ({
          id: s.id,
          name: s.title || s.name,
          desc: s.short_description || s.description || '',
          image: s.image || null,
          slug: s.slug || s.id,
        })));
      }
    });
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => { if(e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.1, rootMargin: '0px 0px -30px 0px' }
    );
    const els = sectionRef.current.querySelectorAll('.rv');
    els.forEach(el => observer.observe(el));
    return () => els.forEach(el => observer.unobserve(el));
  }, [services]);

  // Autoplay
  useEffect(() => {
    const interval = setInterval(() => {
      setCenterServiceIndex(prev => (prev >= services.length - 1 ? 0 : prev + 1));
    }, 4500);
    return () => clearInterval(interval);
  }, [services.length]);

  const handlePrev = useCallback(() => {
    setCenterServiceIndex(prev => (prev <= 0 ? services.length - 1 : prev - 1));
  }, [services.length]);

  const handleNext = useCallback(() => {
    setCenterServiceIndex(prev => (prev >= services.length - 1 ? 0 : prev + 1));
  }, [services.length]);

  return (
    <section id="services" ref={sectionRef}>
      <div className="svc-watermark">Services</div>
      <div className="svc-container">
        {/* Top Block: Two-column grid */}
        <div className="svc-top-grid rv">
          <div className="svc-top-left">
            <div className="svc-eyebrow">
              <span className="svc-eyebrow-line"></span>
              <span className="svc-eyebrow-text">WHAT WE OFFER</span>
            </div>
            <h2 className="svc-heading">
              {(() => {
                const words = sectionHeading.split(' ');
                if (words.length >= 2) {
                  return <>{words.slice(0, -1).join(' ')} <span className="svc-heading-accent">{words[words.length - 1]}</span></>;
                }
                return <span className="svc-heading-accent">{sectionHeading}</span>;
              })()}
            </h2>
            <p className="svc-subtitle">{sectionDesc}</p>
          </div>
          <div className="svc-top-right">
            <div className="svc-checklist">
              {whyPoints.map((point, i) => (
                <div key={i} className="svc-checklist-item">
                  <span className="svc-checklist-icon">✓</span>
                  <span className="svc-checklist-text">{point}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Block: 3 cards sliding horizontally */}
        <div className="svc-cards-wrapper rv d2">
          <div className="svc-cards-track">
            {services.map((svc, i) => {
              const len = services.length;
              const diff = ((i - centerServiceIndex) % len + len) % len;
              let position;
              if (diff === 0) position = 'center';
              else if (diff === 1) position = 'right';
              else if (diff === len - 1) position = 'left';
              else if (diff === 2 || diff === len - 2) position = diff <= len / 2 ? 'far-right' : 'far-left';
              else position = 'hidden';

              return (
                <div
                  className={`svc-card-slide svc-card-slide--${position}`}
                  key={svc.id || i}
                >
                  <Link to={`/services/${svc.slug}`} style={{textDecoration:'none',color:'inherit',display:'block',width:'100%',height:'100%'}}>
                    <div className="svc-card">
                      <div className="svc-card-img-wrap">
                        {svc.image && <img className="svc-card-img" src={svc.image} alt={svc.name} loading="lazy" />}
                        <div className="svc-card-overlay"></div>
                      </div>
                      <div className="svc-card-body">
                        <div className="svc-card-title">{svc.name}</div>
                        <p className="svc-card-desc">{svc.desc}</p>
                      </div>
                    </div>
                  </Link>
                </div>
              );
            })}
          </div>

          {/* Navigation arrows below cards */}
          <div className="svc-nav-bottom">
            <button className="svc-nav-btn" onClick={handlePrev} aria-label="Previous">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
            </button>
            <button className="svc-nav-btn" onClick={handleNext} aria-label="Next">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="9 6 15 12 9 18"></polyline>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
